import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from './Toast';

const ArtworkCard = ({ artwork }) => {
  const { addToCart, isInCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      showToast('Please login to add items to cart', 'warning');
      return;
    }

    const result = addToCart(artwork);
    if (result.success) {
      showToast('Added to cart!', 'success');
    } else {
      showToast(result.message, 'info');
    }
  };

  return (
    <Link to={`/artwork/${artwork._id}`} className="group">
      <div className="art-card h-full">
        {/* Image Container */}
        <div className="relative overflow-hidden aspect-square">
          <img
            src={artwork.images?.[0] || 'https://placehold.co/400x400/fb923c/ffffff?text=Artwork'}
            alt={artwork.title}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
            onError={(e) => {
              e.target.src = 'https://placehold.co/400x400/fb923c/ffffff?text=Artwork';
            }}
          />
          
          {/* Overlay on Hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <button
                onClick={handleAddToCart}
                disabled={isInCart(artwork._id) || !artwork.inStock}
                className={`w-full py-2 rounded-lg font-semibold transition-all duration-300 transform translate-y-4 group-hover:translate-y-0 ${
                  isInCart(artwork._id) || !artwork.inStock
                    ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                    : 'bg-white text-orange-600 hover:bg-orange-600 hover:text-white'
                }`}
              >
                {!artwork.inStock ? 'Sold Out' : isInCart(artwork._id) ? 'In Cart' : 'Add to Cart'}
              </button>
            </div>
          </div>

          {/* Featured Badge */}
          {artwork.featured && (
            <div className="absolute top-3 right-3 bg-gradient-to-r from-orange-500 to-red-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
              Featured
            </div>
          )}

          {/* Category Badge */}
          <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-orange-900 capitalize">
            {artwork.category?.replace('-', ' ')}
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-3">
          <h3 className="text-xl font-display font-bold text-gray-900 group-hover:text-orange-600 transition-colors duration-200 line-clamp-1">
            {artwork.title}
          </h3>
          
          <p className="text-sm text-gray-600 line-clamp-2">
            {artwork.description}
          </p>

          {/* Artist Info */}
          <div className="flex items-center space-x-2 pt-2">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
              {artwork.artist?.name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div>
              <p className="text-xs text-gray-500">by</p>
              <p className="text-sm font-semibold text-gray-900">{artwork.artist?.name || 'Unknown Artist'}</p>
            </div>
          </div>

          {/* Price and Details */}
          <div className="flex items-center justify-between pt-3 border-t border-orange-100">
            <div>
              <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                ${artwork.price?.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span>{artwork.views || 0} views</span>
            </div>
          </div>

          {/* Tags */}
          {artwork.tags && artwork.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {artwork.tags.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ArtworkCard;